/**
 * Formát termínů prohlídky (krok CRM4) — JEDNO místo pro veřejnou stránku,
 * kartu nemovitosti, „Prohlídky" i Dnešek.
 *
 * Časové pásmo je PEVNĚ Europe/Prague: prohlídka se koná u nemovitosti
 * v ČR a zájemce z ciziny má vidět místní čas, ne čas svého prohlížeče
 * (tentýž princip jako „papírový čas" připomínek CRM3, `formatReminderMoment`).
 */

const ZONE = 'Europe/Prague';

export function viewingDayKey(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(iso),
  );
}

export function formatViewingDay(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'cs-CZ', {
    timeZone: ZONE,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(iso));
}

export function formatViewingTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'cs-CZ', {
    timeZone: ZONE,
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function formatViewingRange(startsAt: string, endsAt: string, locale: string): string {
  return `${formatViewingDay(startsAt, locale)}, ${formatViewingTime(startsAt, locale)}–${formatViewingTime(endsAt, locale)}`;
}

/** Termíny seskupené po dnech (v pořadí, jak přišly — server řadí). */
export function groupSlotsByDay<T extends { readonly startsAt: string }>(
  slots: readonly T[],
): { readonly day: string; readonly items: readonly T[] }[] {
  const groups = new Map<string, T[]>();
  for (const slot of slots) {
    const key = viewingDayKey(slot.startsAt);
    const list = groups.get(key) ?? [];
    list.push(slot);
    groups.set(key, list);
  }
  return [...groups.entries()].map(([day, items]) => ({ day, items }));
}

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Kontrast barvy s bílou (WCAG). Neplatná barva = 0. */
export function contrastWithWhite(hex: string): number {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (m === null) return 0;
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => channel(parseInt(h ?? '0', 16)));
  const lum = 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0);
  return 1.05 / (lum + 0.05);
}

/**
 * Akcent kanceláře pro veřejnou stránku — jen když má s bílým textem
 * kontrast ≥ 4,5:1 (DESIGN-UX-V4 „Tokeny"). Jinak zůstane akcent REOS.
 */
export function officeAccentStyle(hex: string | null): Record<string, string> {
  if (hex === null || contrastWithWhite(hex) < 4.5) return {};
  return { '--rh-accent': hex, '--rh-accent-fill': hex };
}
